import { useColorMode } from '@vueuse/core'
import { defineStore } from 'pinia'
import { ref } from 'vue'

import i18n from '@/libs/i18n'
import { clearModuleRefusal } from '@/libs/license'
import type { IProperty } from '@/types/Property'

/**
 * Where the session token is kept, and why there are two places.
 *
 * „Ostani prijavljen“ is the whole difference. Ticked, the token goes to `localStorage`
 * and survives the telephone being locked, the browser being closed and the handset
 * being restarted — which is what somebody wants on their OWN phone, where the
 * alternative is typing a password at six in the morning with gloves on. Unticked, it
 * goes to `sessionStorage` and is gone when the tab is closed, which is what somebody
 * wants on a handset that is not theirs.
 *
 * Both are read on start-up and the local one wins, so a person who once ticked the box
 * stays signed in until they sign out.
 *
 * # What it does NOT do, said plainly
 *
 * The service also sets its own `JWT` cookie at sign-in, HttpOnly and with a lifetime of
 * its own (`HTTP_API/Authentication.go`) — it is what makes a document download work,
 * because a browser cannot put an Authorization header on an `<a href>`. This page
 * cannot read it and cannot shorten it. So unticking the box means THIS PORTAL forgets
 * the session; the cookie sits there unused until the server's own lifetime runs out or
 * somebody signs out, which clears it at the server. On a shared handset the honest
 * instruction is still „odjavi se“, and the sign-out button is where every screen can
 * reach it.
 */
const TOKEN_KEY    = 'JWT'
const REMEMBER_KEY = 'pms:staff-remember'

const readToken = (): string => {
    try {
        return localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY) || ''
    } catch {
        // A browser refusing storage — a private window, a locked-down handset. The
        // portal then works for exactly as long as the tab is open, which is a real
        // state and not a failure worth a screen of its own.
        return ''
    }
}

export interface LoggedInUserData {
    id:       string
    name:     string
    username: string

    /**
     * The language on the ACCOUNT, as `GET /me` answers it.
     *
     * It is the default this portal opens in — a Croatian housekeeper should not have
     * to find a language button before she can read her own week — and it is a default
     * rather than a setting: what the person picks on the device wins from then on. See
     * `changeLocale`.
     */
    language: string

    /**
     * What this account may do, as answered by `GET /me`.
     *
     * Almost nothing on this portal branches on it, and that is §13 rather than an
     * omission: reading your own record is the other side of BEING in it, not a right
     * somebody grants. It is held because the topbar has one thing to say about it —
     * whether to offer the link across to the reception portal — and because a screen
     * that ever does need to ask must not invent a second way of asking.
     */
    permissions: string[]
}

export const useAppStore = defineStore('app', () => {
    const JWT = ref(readToken())

    /**
     * The language on the screen, and it is NEVER empty.
     *
     * It mirrors what `libs/i18n` was created with, which is the stored choice or `sl` —
     * and the fallback matters: the top bar draws `nav.language_${locale}` and an empty
     * ref asks for `nav.language_`, which exists in no locale file and renders as the key
     * itself. Whether somebody has actually CHOSEN a language is a different question and
     * is asked of `localStorage` directly, in `libs/session.ts`, where it decides whether
     * the account's own language may be applied.
     */
    const locale = ref(localStorage.getItem('locale') || 'sl')
    const windowWidth  = ref(window.innerWidth)
    const loaderActive = ref(0)
    const userData     = ref<LoggedInUserData>()

    /**
     * The houses this account works in, and which of them is on the screen.
     *
     * Almost nothing here needs one: every personal read is outside `/properties`
     * because somebody who works two hotels has ONE record (docs/EVIDENCA-CASA.md §2).
     * What the house IS needed for is the two facts that belong to a hotel rather than
     * to a person — whether a month has been signed off, and which decision a leave PDF
     * came from — and for saying, on the profile card, which house somebody is standing
     * in when they have more than one.
     *
     * Resolved from `GET /me`, which names them, rather than from `/properties`, which
     * is behind `property:read` and which nobody on this portal holds.
     */
    const propertyId = ref(localStorage.getItem('propertyId') || '')
    const properties = ref<IProperty[]>([])

    /** Which modules this hotel holds, as `GET /modules` answers them. */
    const modules = ref<string[]>([])

    /**
     * Whether `GET /me` has answered for this token. The router waits on it, so no
     * screen renders before the portal knows whose it is.
     */
    const sessionLoaded = ref(false)

    // Wraps the SAME state @nuxt/ui's vue plugin already manages via useDark() — never a
    // second source of truth. Values: 'auto' | 'light' | 'dark'.
    //
    // emitAuto is what makes 'auto' readable. Without it VueUse resolves the stored
    // preference before handing it over, so an account set to 'auto' on a device whose
    // system is dark reads back as 'dark' — and a three-state button could never leave
    // it: it saw 'dark', wrote 'auto', read 'dark' again. On a light device the same
    // code appeared to work. The class on <html> still follows the RESOLVED mode; only
    // what is read here is the preference itself.
    const colorMode = useColorMode({ storageKey: 'vueuse-color-scheme', emitAuto: true })

    window.addEventListener('resize', () => { windowWidth.value = window.innerWidth })

    const getJWT         = () => JWT.value
    const isLoggedIn     = () => !!JWT.value
    const isLoaderActive = () => loaderActive.value > 0
    const isMobile       = () => windowWidth.value < 1024

    /** What this account may do. Unknown until GET /me has answered, so it is false. */
    const can = (permission: string) => !!userData.value?.permissions?.includes(permission)

    /**
     * Whether this hotel holds a module. Unknown until GET /modules has answered, so it
     * is false — the router turns a screen away rather than letting its first call come
     * back 403 with code 19, which locks the whole portal (libs/license.ts).
     */
    const hasModule = (key: string) => modules.value.includes(key)

    /** The house on the screen, out of the list the session loaded. */
    const getProperty = () => properties.value.find(property => property.id === propertyId.value)

    /**
     * Signing out, and the cookie the server holds.
     *
     * The session cookie is written by the server and is HttpOnly, so this page cannot
     * remove it — which is the whole point of it being HttpOnly, and the reason signing
     * out has to ask the server. It is asked and not waited on: whatever the network
     * says, this browser is signed out the moment somebody presses the button.
     *
     * It is the one call in this portal that does NOT go through the fetch store, for
     * the reason CLAUDE.md deviation 16 gives about the reception portal's three: the
     * fetch store reads its token FROM this store, so a call made from here through it
     * is a cycle. Spelled `window.fetch` to name the platform primitive on purpose —
     * the same spelling and the same rule as there, and nothing else here may do it.
     */
    const clearServerSession = () => {
        void window.fetch('/api/pms/v1/logout', { method: 'POST', credentials: 'same-origin' }).catch(() => {})
    }

    const setLogin = (jwt: string, remember: boolean) => {
        try {
            // Written to exactly one of the two, and the other is cleared — a token left
            // in localStorage by a previous „ostani prijavljen“ would outlive a
            // deliberate decision not to be remembered, which is the one thing this
            // choice exists to prevent.
            localStorage.removeItem(TOKEN_KEY)
            sessionStorage.removeItem(TOKEN_KEY)

            if (remember) {
                localStorage.setItem(TOKEN_KEY, jwt)
                localStorage.setItem(REMEMBER_KEY, '1')
            } else {
                sessionStorage.setItem(TOKEN_KEY, jwt)
                localStorage.removeItem(REMEMBER_KEY)
            }
        } catch {
            // Storage refused. The token still lives in memory below, so this session
            // works and simply does not survive a reload.
        }

        JWT.value = jwt

        // A refusal was recorded for whoever was signed in before. This is somebody
        // else — or the same person after the hotel bought the module — so the portal
        // asks again rather than showing them the previous account's locked door.
        clearModuleRefusal()
    }

    /** Whether the box was ticked last time, so the login screen opens with it as it was. */
    const wasRemembered = (): boolean => {
        try {
            return localStorage.getItem(REMEMBER_KEY) === '1'
        } catch {
            return false
        }
    }

    const setLogout = () => {
        try {
            localStorage.removeItem(TOKEN_KEY)
            sessionStorage.removeItem(TOKEN_KEY)
        } catch {
            // Nothing depends on it having worked; the refs below are what the portal reads.
        }

        JWT.value        = ''
        userData.value   = undefined
        properties.value = []
        modules.value    = []
        sessionLoaded.value = false

        // Signing out ends the only identity the refusal was about. Leaving it set
        // would greet the next person at this handset with a locked door that was never
        // theirs, and the login screen underneath would never be reached.
        clearModuleRefusal()

        clearServerSession()
    }

    const setPropertyId = (id: string) => {
        propertyId.value = id
        localStorage.setItem('propertyId', id)
    }

    const setUserData   = (data: LoggedInUserData | undefined) => userData.value = data
    const setProperties = (list: IProperty[]) => properties.value = list
    const setModules    = (list: string[]) => modules.value = list

    const startLoading = () => loaderActive.value++
    const stopLoading  = () => loaderActive.value = Math.max(0, loaderActive.value - 1)

    /**
     * The language this device is read in.
     *
     * `remembered` says whether the person CHOSE it. The account's own language is
     * applied on sign-in and must not overwrite a choice made on the handset afterwards
     * — somebody whose card says `sl` and who reads `hr` has said so once and should not
     * have to say it again every morning (libs/session.ts).
     */
    const changeLocale = (newLocale: string, remembered = true) => {
        locale.value = newLocale

        if (remembered) localStorage.setItem('locale', newLocale)

        ;(i18n.global.locale as unknown as { value: string }).value = newLocale
    }

    return {
        JWT, locale, userData, loaderActive, colorMode, propertyId, properties, modules, sessionLoaded,
        getJWT, isLoggedIn, isLoaderActive, isMobile, can, hasModule, getProperty, wasRemembered,
        setLogin, setLogout, setPropertyId, setUserData, setProperties, setModules,
        startLoading, stopLoading, changeLocale,
    }
})
